import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { Category, CategoryFormData, CategoryImportSummary, PagedResult } from './category.model';
import { ConfigService } from '../../../core/service/config.service';
import { HsPrefixSuggestResult } from '../../../core/modules/hs-review/hs-review.model';

@Injectable({ providedIn: 'root' })
export class CategoriesService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) {}

  getAll(page: number = 1, pageSize: number = 100, searchCriteria?: string, showInHomePage?: boolean): Observable<PagedResult<Category>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (searchCriteria) {
      params = params.set('searchCriteria', searchCriteria);
    }
    if (showInHomePage !== undefined) {
      params = params.set('showInHomePage', showInHomePage.toString());
    }

    return this.http.get<any>(this.configService.getAllCategories, { params }).pipe(
      map(res => res?.data ?? { items: [], pageNumber: page, pageSize, totalCount: 0, totalPages: 0 })
    );
  }

  getSubcategories(id: string): Observable<Category[]> {
    return this.http.get<any>(this.configService.getCategorySubcategories(id)).pipe(
      map(res => res?.data ?? [])
    );
  }

  getById(id: string): Observable<Category> {
    return this.http.get<any>(this.configService.getCategoryById(id)).pipe(
      map(res => res?.data ?? null)
    );
  }

  create(formData: CategoryFormData): Observable<any> {
    const data = this.buildFormData(formData);
    return this.http.post<any>(this.configService.createCategory, data);
  }

  update(id: string, formData: CategoryFormData): Observable<any> {
    const data = this.buildFormData(formData);
    return this.http.put<any>(this.configService.updateCategory(id), data);
  }

  delete(id: string): Observable<any> {
    return this.http.delete<any>(this.configService.deleteCategory(id));
  }

  importFile(file: File): Observable<CategoryImportSummary> {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.post<any>(this.configService.importCategoriesUrl, fd).pipe(
      map(res => res?.data)
    );
  }

  downloadTemplate(): Observable<Blob> {
    return this.http.get(this.configService.importCategoriesTemplateUrl, { responseType: 'blob' });
  }

  suggestPrefixes(title: string, description?: string | null): Observable<HsPrefixSuggestResult> {
    const body: any = { title, maxHeadings: 8 };
    if (description) body['description'] = description;
    return this.http
      .post<any>(this.configService.adminHsPrefixSuggestionsUrl, body)
      .pipe(map((res) => res?.data));
  }

  private buildFormData(formData: CategoryFormData): FormData {
    const fd = new FormData();
    fd.append('titleEn', formData.titleEn);
    fd.append('titleAr', formData.titleAr);

    // The update is partial: an omitted field is left unchanged, so clearing
    // the parent needs moveToTopLevel and the home-page flag is always sent.
    if (formData.parentId) {
      fd.append('parentId', formData.parentId);
    } else if (formData.moveToTopLevel) {
      fd.append('moveToTopLevel', 'true');
    }

    if (formData.showInHomePage != null) {
      fd.append('showInHomePage', String(formData.showInHomePage));
    }

    if (formData.image) {
      fd.append('image', formData.image);
    }

    if (formData.circularImage) {
      fd.append('circularImage', formData.circularImage);
    }

    if (formData.hsAllowedPrefixes) {
      fd.append('hsAllowedPrefixes', formData.hsAllowedPrefixes);
    }

    return fd;
  }
}
